/**
 * Cookie consent: types and client-side read/write for the consent cookie.
 * Used by CookieConsentContext and CookiePreferences.
 */

export const COOKIE_CONSENT_NAME = "cookie_consent";
const MAX_AGE_DAYS = 365;

export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

export function getDefaultConsent(): CookieConsent {
  return { necessary: true, analytics: false, marketing: false };
}

export function parseConsentCookie(value: string | undefined): CookieConsent | null {
  if (!value?.trim()) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    if (typeof parsed !== "object" || parsed === null) return null;
    const p = parsed as Record<string, unknown>;
    if (
      p.necessary === true &&
      typeof p.analytics === "boolean" &&
      typeof p.marketing === "boolean"
    ) {
      return parsed as CookieConsent;
    }
  } catch {
    // ignore
  }
  return null;
}

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Read consent from cookie. Returns null if no valid consent cookie (user has not chosen yet). */
export function readConsent(): CookieConsent | null {
  return parseConsentCookie(getCookie(COOKIE_CONSENT_NAME));
}

/** Write consent to cookie (client-only). */
export function writeConsent(consent: CookieConsent): void {
  if (typeof document === "undefined") return;
  const value = encodeURIComponent(JSON.stringify(consent));
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${COOKIE_CONSENT_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/** Clear the consent cookie (client-only). After this, the banner will show again on next read. */
export function clearConsent(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_CONSENT_NAME}=; path=/; max-age=0; SameSite=Lax`;
}
